import React, { useState } from 'react';
import { CheckCircle, Car, Fuel, Shield, Users, Clock, Phone, Mail, MapPin, ChevronRight, X, ChevronLeft, ChevronRight as ChevronRightIcon } from 'lucide-react';

const VTCLanding = () => {
  const [formData, setFormData] = useState({ name: '', phone: '', message: '', email: '' });
  const [submitStatus, setSubmitStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [currentVehicleIndex, setCurrentVehicleIndex] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const phoneNumber = "0650972620";
  const telLink = "tel:+33650972620";

  // Fleet vehicles data
  const vehicles = [
    {
      id: 1,
      name: "Toyota C-HR Hybride 140",
      color: "Bleu foncé",
      colorBadgeClass: "bg-blue-900/30 border-blue-500 text-blue-400",
      description: "Toyota C-HR hybride moderne, économique et parfaitement adaptée à l'activité VTC. Un véhicule confortable et fiable, idéal pour travailler avec Uber, Bolt ou Heetch en Île-de-France.",
      images: [
        {
          url: "https://customer-assets.emergentagent.com/job_premium-audio-59/artifacts/lvhv4b4q_t1.avif",
          alt: "Toyota C-HR Hybride 140 Bleu foncé - Vue de face",
          title: "Vue de face"
        },
        {
          url: "https://customer-assets.emergentagent.com/job_premium-audio-59/artifacts/wickk7c8_t2.avif",
          alt: "Toyota C-HR Hybride 140 Bleu foncé - Vue arrière",
          title: "Vue arrière"
        },
        {
          url: "https://customer-assets.emergentagent.com/job_premium-audio-59/artifacts/y6ssanye_t3.avif",
          alt: "Toyota C-HR Hybride 140 Bleu foncé - Intérieur",
          title: "Intérieur avec Apple CarPlay"
        }
      ]
    },
    {
      id: 2,
      name: "Toyota C-HR Hybride 2025",
      color: "Noir",
      colorBadgeClass: "bg-gray-900/30 border-gray-500 text-gray-300",
      description: "Toyota C-HR hybride nouvelle génération, idéale pour les chauffeurs VTC. Un véhicule moderne, confortable et économique parfaitement adapté pour travailler en Île-de-France.",
      images: [
        {
          url: "https://customer-assets.emergentagent.com/job_premium-audio-59/artifacts/3rbzpu5t_t4.avif",
          alt: "Toyota C-HR Hybride 2025 Noir - Vue de face",
          title: "Vue de face"
        },
        {
          url: "https://customer-assets.emergentagent.com/job_premium-audio-59/artifacts/xrsge5kt_t5.avif",
          alt: "Toyota C-HR Hybride 2025 Noir - Vue arrière",
          title: "Vue arrière"
        },
        {
          url: "https://customer-assets.emergentagent.com/job_premium-audio-59/artifacts/wljolxey_t6.avif",
          alt: "Toyota C-HR Hybride 2025 Noir - Intérieur",
          title: "Intérieur avec Apple CarPlay"
        }
      ]
    },
    {
      id: 3,
      name: "Toyota C-HR Hybride 2025",
      color: "Blanc",
      colorBadgeClass: "bg-gray-100/10 border-gray-300 text-gray-200",
      description: "Toyota C-HR hybride nouvelle génération, idéale pour l'activité VTC. Un véhicule moderne, économique et confortable parfaitement adapté pour travailler en Île-de-France.",
      images: [
        {
          url: "https://customer-assets.emergentagent.com/job_premium-audio-59/artifacts/iyg4p3a0_Image%2009-03-2026%20a%CC%80%2023.58.png",
          alt: "Toyota C-HR Hybride 2025 Blanc - Vue de face",
          title: "Vue de face"
        },
        {
          url: "https://customer-assets.emergentagent.com/job_premium-audio-59/artifacts/tb0g5803_Image%2010-03-2026%20a%CC%80%2000.08.png",
          alt: "Toyota C-HR Hybride 2025 Blanc - Vue arrière",
          title: "Vue arrière"
        },
        {
          url: "https://customer-assets.emergentagent.com/job_premium-audio-59/artifacts/ek2hytjb_Image%2010-03-2026%20a%CC%80%2000.08.png",
          alt: "Toyota C-HR Hybride 2025 Blanc - Intérieur",
          title: "Intérieur avec Apple CarPlay"
        }
      ]
    }
  ];

  const openGallery = (vehicleIndex, imageIndex = 0) => {
    setCurrentVehicleIndex(vehicleIndex);
    setCurrentImageIndex(imageIndex);
    setGalleryOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeGallery = () => {
    setGalleryOpen(false);
    document.body.style.overflow = 'auto';
  };

  const nextImage = () => {
    const currentVehicle = vehicles[currentVehicleIndex];
    setCurrentImageIndex((prev) => (prev + 1) % currentVehicle.images.length);
  };

  const prevImage = () => {
    const currentVehicle = vehicles[currentVehicleIndex];
    setCurrentImageIndex((prev) => (prev - 1 + currentVehicle.images.length) % currentVehicle.images.length);
  };

  // Dynamic API URL - works on both preview and production domains
  const getApiBaseUrl = () => {
    // Use relative URL - this works on any domain as the backend is served from the same origin
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const apiBaseUrl = getApiBaseUrl();
      const apiUrl = `${apiBaseUrl}/api/contact`;
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', phone: '', message: '', email: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCallClick = (e) => {
    // On desktop, show alert with phone number
    if (window.innerWidth >= 768) {
      e.preventDefault();
      alert(`Appelez-nous au ${phoneNumber.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5')}`);
    }
    // On mobile, the tel: link will work automatically
  };

  // Premium Call Button Component
  const PremiumCallButton = ({ showMessage = true, size = "large", className = "" }) => {
    const isLarge = size === "large";
    
    return (
      <div className={`text-center ${className}`}>
        {showMessage && (
          <p className="text-green-600 font-semibold text-sm mb-3 animate-pulse">
            ⚡ Disponibilités limitées – Contactez-nous maintenant
          </p>
        )}
        <a 
          href={telLink}
          onClick={handleCallClick}
          className={`call-button-premium inline-flex flex-col items-center justify-center bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-2xl font-bold transition-all duration-300 shadow-lg hover:shadow-xl ${
            isLarge ? 'px-10 py-6 gap-1' : 'px-6 py-4 gap-1'
          }`}
        >
          <div className="flex items-center gap-3">
            <Phone className={`${isLarge ? 'w-6 h-6' : 'w-5 h-5'} animate-pulse`} />
            <span className={`${isLarge ? 'text-2xl' : 'text-xl'}`}>Appeler maintenant</span>
          </div>
          <span className={`${isLarge ? 'text-lg' : 'text-base'} opacity-90 font-normal`}>
            06 50 97 26 20
          </span>
        </a>
      </div>
    );
  };

  // Text Logo Component
  const TextLogo = ({ size = 'default' }) => {
    const isLarge = size === 'large';
    const isMedium = size === 'medium';
    
    return (
      <div className="flex items-baseline gap-1.5 select-none">
        <span 
          className={`font-black tracking-tight text-black ${
            isLarge ? 'text-4xl' : isMedium ? 'text-2xl' : 'text-xl md:text-3xl'
          }`}
          style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
        >
          LOCATION
        </span>
        <span 
          className={`font-bold text-black ${
            isLarge ? 'text-3xl' : isMedium ? 'text-xl' : 'text-lg md:text-2xl'
          }`}
          style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
        >
          VTC
        </span>
        <span 
          className={`font-bold text-green-500 ${
            isLarge ? 'text-3xl' : isMedium ? 'text-xl' : 'text-lg md:text-2xl'
          }`}
          style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
        >
          IDF
        </span>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-md z-50 shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20 md:h-24">
            <a href="/" className="flex items-center">
              <TextLogo />
            </a>
            <div className="hidden md:flex space-x-8 items-center">
              <a href="#avantages" className="text-gray-600 hover:text-green-500 transition">Avantages</a>
              <a href="#vehicules" className="text-gray-600 hover:text-green-500 transition">Véhicules</a>
              <a href="#fonctionnement" className="text-gray-600 hover:text-green-500 transition">Fonctionnement</a>
              <a href="#contact" className="text-gray-600 hover:text-green-500 transition">Contact</a>
              <a 
                href={telLink} 
                onClick={handleCallClick}
                className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-full font-semibold transition inline-flex items-center gap-2 shadow-md"
              >
                <Phone className="w-4 h-4" />
                Appeler maintenant
              </a>
            </div>
            <a href="#contact" className="md:hidden bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-full font-semibold transition shadow-md">
              Contact
            </a>
          </div>
        </div>
      </nav>

      {/* Floating Call Button (Mobile Only) - Premium Version */}
      <a
        href={telLink}
        className="md:hidden fixed bottom-6 right-6 z-40 call-button-premium bg-gradient-to-r from-green-500 to-green-600 text-white p-5 rounded-full shadow-xl transition-all hover:scale-110 flex items-center justify-center animate-float"
        aria-label="Appeler maintenant"
      >
        <Phone className="w-7 h-7 animate-pulse" />
      </a>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_premium-audio-59/artifacts/gmiynoqk_Toyota%20couverture%20leboncon.avif" 
            alt="Location VTC Île-de-France Toyota C-HR hybride Paris Tour Eiffel"
            className="w-full h-full object-cover object-center md:object-[center_60%] animate-slow-zoom"
            style={{ objectPosition: 'center 70%' }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent"></div>
        </div>
        
        <div className="relative z-10 text-left px-4 sm:px-8 max-w-7xl mx-auto w-full pt-20 lg:pt-28">
          <div className="max-w-2xl">
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold mb-6 leading-tight">
              Location VTC en Île-de-France<br />
              <span className="text-green-500">Toyota C-HR Hybrides disponibles</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-200 mb-4 font-semibold">
              Location de voiture VTC pour chauffeurs Uber, Bolt, Heetch et FreeNow
            </p>
            <p className="text-base sm:text-lg text-gray-300 mb-8">
              Véhicules hybrides économiques • KM illimité • Sans caution • Disponibles immédiatement
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <a href="#vehicules" className="bg-green-500 hover:bg-green-600 px-8 py-4 rounded-full font-semibold text-lg transition inline-flex items-center justify-center shadow-lg">
                Voir les véhicules <ChevronRight className="ml-2" />
              </a>
              <a 
                href={telLink} 
                onClick={handleCallClick}
                className="bg-white/10 hover:bg-white/20 backdrop-blur-sm px-8 py-4 rounded-full font-semibold text-lg transition border border-white/30 inline-flex items-center justify-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Appeler maintenant – 06 50 97 26 20
              </a>
            </div>
            <p className="text-sm text-green-400 font-semibold">
              Réponse rapide – Disponibilités limitées
            </p>
          </div>
        </div>
      </section>

      {/* Avantages Section */}
      <section id="avantages" className="py-24 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-center mb-4 text-gray-900">
            Pourquoi choisir nos <span className="text-green-500">véhicules VTC</span> ?
          </h2>
          <p className="text-center text-gray-500 mb-16 max-w-3xl mx-auto">
            Nos véhicules Toyota C-HR hybrides sont parfaitement adaptés à la location VTC en Île-de-France pour les chauffeurs travaillant avec Uber, Bolt, Heetch ou FreeNow.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-lg transition border border-gray-100 hover:border-green-200">
              <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center mb-6">
                <Car className="w-7 h-7 text-green-500" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Toyota C-HR Hybrides Récentes</h3>
              <p className="text-gray-500">Véhicules modernes et bien entretenus, parfaits pour une activité VTC professionnelle</p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-lg transition border border-gray-100 hover:border-green-200">
              <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center mb-6">
                <Clock className="w-7 h-7 text-green-500" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Kilométrage Illimité</h3>
              <p className="text-gray-500">Roulez autant que vous le souhaitez sans frais supplémentaires</p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-lg transition border border-gray-100 hover:border-green-200">
              <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center mb-6">
                <Shield className="w-7 h-7 text-green-500" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Aucune Caution</h3>
              <p className="text-gray-500">Démarrez votre activité sans blocage de capital</p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-lg transition border border-gray-100 hover:border-green-200">
              <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center mb-6">
                <Fuel className="w-7 h-7 text-green-500" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Véhicules Économiques</h3>
              <p className="text-gray-500">Technologie hybride pour réduire vos coûts de carburant</p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-lg transition border border-gray-100 hover:border-green-200">
              <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center mb-6">
                <Users className="w-7 h-7 text-green-500" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Confort Premium</h3>
              <p className="text-gray-500">Intérieur spacieux et confortable pour vos clients</p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-lg transition border border-gray-100 hover:border-green-200">
              <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center mb-6">
                <CheckCircle className="w-7 h-7 text-green-500" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Entretien Inclus</h3>
              <p className="text-gray-500">Suivi professionnel et maintenance régulière assurés</p>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Content Section - Location VTC Île-de-France */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold mb-8 text-center text-gray-900">
            Pourquoi louer un <span className="text-green-500">véhicule VTC</span> chez nous ?
          </h2>
          
          <div className="prose max-w-none">
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              Vous êtes chauffeur VTC et recherchez une <strong className="text-gray-900">location de voiture VTC en Île-de-France</strong> ? 
              Notre entreprise est spécialisée dans la <strong className="text-gray-900">location VTC</strong> de Toyota C-HR hybrides pour les chauffeurs professionnels 
              travaillant avec <strong className="text-gray-900">Uber</strong>, <strong className="text-gray-900">Bolt</strong>, <strong className="text-gray-900">Heetch</strong> et <strong className="text-gray-900">FreeNow</strong>.
            </p>

            <div className="bg-gray-50 p-8 rounded-2xl border border-gray-100 mb-8">
              <h3 className="text-2xl font-bold mb-6 text-gray-900">Nos avantages pour la location VTC</h3>
              <ul className="space-y-4 text-gray-600">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-500 mt-1 flex-shrink-0" />
                  <span><strong className="text-gray-900">Toyota C-HR hybrides récentes</strong> - Véhicules modernes parfaitement adaptés à l'activité VTC en Île-de-France</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-500 mt-1 flex-shrink-0" />
                  <span><strong className="text-gray-900">Faible consommation</strong> - Technologie hybride pour réduire vos coûts de carburant</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-500 mt-1 flex-shrink-0" />
                  <span><strong className="text-gray-900">Kilométrage illimité</strong> - Roulez autant que nécessaire sans frais supplémentaires</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-500 mt-1 flex-shrink-0" />
                  <span><strong className="text-gray-900">Aucune caution</strong> - Démarrez votre activité VTC sans blocage de capital</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-500 mt-1 flex-shrink-0" />
                  <span><strong className="text-gray-900">Confort premium</strong> - Intérieur spacieux et confortable pour vos clients</span>
                </li>
              </ul>
            </div>

            <h3 className="text-2xl font-bold mb-6 text-gray-900">Zone d'activité : Location VTC en Île-de-France</h3>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              Nos véhicules sont disponibles pour les chauffeurs VTC travaillant dans toute l'<strong className="text-gray-900">Île-de-France</strong>, notamment :
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                <p className="text-gray-900 font-semibold">✓ Paris (75)</p>
                <p className="text-gray-500 text-sm">Location VTC Paris centre et périphérie</p>
              </div>
              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                <p className="text-gray-900 font-semibold">✓ Seine-et-Marne (77)</p>
                <p className="text-gray-500 text-sm">Location voiture VTC Seine-et-Marne</p>
              </div>
              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                <p className="text-gray-900 font-semibold">✓ Essonne (91)</p>
                <p className="text-gray-500 text-sm">Location VTC Essonne</p>
              </div>
              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                <p className="text-gray-900 font-semibold">✓ Val-de-Marne (94)</p>
                <p className="text-gray-500 text-sm">Location voiture Uber Val-de-Marne</p>
              </div>
              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                <p className="text-gray-900 font-semibold">✓ Seine-Saint-Denis (93)</p>
                <p className="text-gray-500 text-sm">Location VTC Seine-Saint-Denis</p>
              </div>
              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                <p className="text-gray-900 font-semibold">✓ Hauts-de-Seine (92)</p>
                <p className="text-gray-500 text-sm">Location voiture VTC Hauts-de-Seine</p>
              </div>
            </div>

            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              Que vous recherchiez une <strong className="text-gray-900">location voiture Uber</strong>, une <strong className="text-gray-900">voiture Bolt à louer</strong> ou 
              un <strong className="text-gray-900">véhicule VTC pour Heetch</strong>, nos Toyota C-HR hybrides sont parfaitement adaptés à votre activité de chauffeur VTC en Île-de-France.
            </p>

            <div className="text-center">
              <PremiumCallButton showMessage={true} size="large" className="mb-6" />
              <p className="text-gray-500">
                <a href="#vehicules" className="text-green-500 hover:text-green-600 underline">
                  Découvrez notre flotte de véhicules VTC disponibles
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Notre Flotte de Véhicules VTC Section */}
      <section id="vehicules" className="py-24 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-center mb-4 text-gray-900">
            Notre flotte de <span className="text-green-500">véhicules VTC</span>
          </h2>
          <p className="text-center text-gray-500 mb-16 max-w-3xl mx-auto">
            Découvrez nos Toyota C-HR hybrides disponibles à la location pour votre activité VTC en Île-de-France
          </p>

          {/* Vehicle Cards */}
          <div className="max-w-4xl mx-auto space-y-8">
            {vehicles.map((vehicle, vehicleIndex) => (
              <div key={vehicle.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300 shadow-lg">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                  {/* Image Section */}
                  <div 
                    className="relative h-80 lg:h-auto cursor-pointer group overflow-hidden"
                    onClick={() => openGallery(vehicleIndex, 0)}
                  >
                    <img 
                      src={vehicle.images[0].url}
                      alt={vehicle.images[0].alt}
                      className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-green-500 text-white px-6 py-3 rounded-full font-semibold">
                        Voir la galerie ({vehicle.images.length} photos)
                      </div>
                    </div>
                    <div className="absolute top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-md">
                      Disponible
                    </div>
                  </div>

                  {/* Info Section */}
                  <div className="p-8 flex flex-col justify-between">
                    <div>
                      <h3 className="text-2xl sm:text-3xl font-bold mb-3 text-gray-900">
                        {vehicle.name}
                      </h3>
                      <div className={`inline-block bg-gray-100 border border-gray-200 text-gray-700 px-4 py-1 rounded-full text-sm font-semibold mb-6`}>
                        {vehicle.color}
                      </div>
                      
                      <p className="text-gray-600 text-lg leading-relaxed mb-6">
                        {vehicle.description}
                      </p>

                      {/* Key Features */}
                      <div className="grid grid-cols-2 gap-4 mb-6">
                        <div className="flex items-center gap-2 text-gray-600">
                          <Fuel className="w-5 h-5 text-green-500" />
                          <span>Hybride 140ch</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <CheckCircle className="w-5 h-5 text-green-500" />
                          <span>KM illimité</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Shield className="w-5 h-5 text-green-500" />
                          <span>Sans caution</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Users className="w-5 h-5 text-green-500" />
                          <span>5 places</span>
                        </div>
                      </div>
                    </div>

                    {/* Call Button */}
                    <PremiumCallButton showMessage={false} size="medium" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Lightbox */}
      {galleryOpen && (() => {
        const currentVehicle = vehicles[currentVehicleIndex];
        const currentImages = currentVehicle.images;
        
        return (
          <div className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4">
            {/* Close Button */}
            <button
              onClick={closeGallery}
              className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition z-10"
              aria-label="Fermer la galerie"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Previous Button */}
            <button
              onClick={prevImage}
              className="absolute left-4 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition z-10"
              aria-label="Image précédente"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            {/* Next Button */}
            <button
              onClick={nextImage}
              className="absolute right-4 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition z-10"
              aria-label="Image suivante"
            >
              <ChevronRightIcon className="w-6 h-6" />
            </button>

            {/* Image Container */}
            <div className="max-w-6xl w-full">
              <div className="relative">
                <img
                  src={currentImages[currentImageIndex].url}
                  alt={currentImages[currentImageIndex].alt}
                  className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
                />
                {/* Image Title */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                  <h3 className="text-white text-xl font-bold mb-1">
                    {currentVehicle.name} - {currentImages[currentImageIndex].title}
                  </h3>
                  <p className="text-gray-300 text-sm">
                    Image {currentImageIndex + 1} sur {currentImages.length}
                  </p>
                </div>
              </div>

              {/* Thumbnails */}
              <div className="flex justify-center gap-4 mt-6">
                {currentImages.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition ${
                      index === currentImageIndex ? 'border-green-500' : 'border-gray-700 hover:border-gray-500'
                    }`}
                  >
                    <img
                      src={img.url}
                      alt={img.title}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Fonctionnement Section */}
      <section id="fonctionnement" className="py-24 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-center mb-4 text-gray-900">
            Comment louer un <span className="text-green-500">véhicule VTC</span> ?
          </h2>
          <p className="text-center text-gray-500 mb-16">Un processus simple et rapide en 5 étapes</p>

          <div className="space-y-8">
            {[
              { num: '01', title: 'Contactez notre équipe', desc: 'Remplissez le formulaire ou appelez-nous directement' },
              { num: '02', title: 'Vérification de votre carte VTC', desc: 'Nous vérifions vos documents et votre carte VTC' },
              { num: '03', title: 'Signature du contrat', desc: 'Contrat de location simple et transparent' },
              { num: '04', title: 'Récupération du véhicule', desc: 'Récupérez votre Toyota C-HR prêt à rouler' },
              { num: '05', title: 'Commencez à travailler', desc: 'Démarrez immédiatement votre activité VTC' }
            ].map((step, idx) => (
              <div key={idx} className="flex gap-6 items-start group">
                <div className="flex-shrink-0 w-16 h-16 bg-green-50 rounded-full flex items-center justify-center border-2 border-green-500 group-hover:bg-green-500 transition">
                  <span className="text-2xl font-bold text-green-500 group-hover:text-white transition">{step.num}</span>
                </div>
                <div className="flex-1 pt-2">
                  <h3 className="text-2xl font-bold mb-2 text-gray-900">{step.title}</h3>
                  <p className="text-gray-500">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Conditions Section */}
      <section className="py-24 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-center mb-4 text-gray-900">
            Conditions de <span className="text-green-500">location</span>
          </h2>
          <p className="text-center text-gray-500 mb-12">Conditions simples et transparentes</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl border border-gray-100 text-center shadow-sm hover:shadow-lg transition">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2 text-gray-900">Carte VTC</h3>
              <p className="text-gray-500">Obligatoire et en cours de validité</p>
            </div>
            <div className="bg-white p-6 rounded-xl border border-gray-100 text-center shadow-sm hover:shadow-lg transition">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2 text-gray-900">Rattachement</h3>
              <p className="text-gray-500">Obligatoire à notre société</p>
            </div>
            <div className="bg-white p-6 rounded-xl border border-gray-100 text-center shadow-sm hover:shadow-lg transition">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2 text-gray-900">Sérieux</h3>
              <p className="text-gray-500">Chauffeurs professionnels uniquement</p>
            </div>
          </div>
        </div>
      </section>

      {/* Zone d'activité Section */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <MapPin className="w-16 h-16 text-green-500 mx-auto mb-6" />
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 text-gray-900">
            Location VTC en <span className="text-green-500">Île-de-France</span>
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Nous proposons la location de voiture VTC en Île-de-France pour les chauffeurs souhaitant travailler à Paris et dans toute la région parisienne. Nos véhicules Toyota C-HR hybrides sont parfaitement adaptés pour une activité de chauffeur VTC avec Uber, Bolt, Heetch ou FreeNow dans toute l'Île-de-France.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-center mb-4 text-gray-900">
            Contactez-nous
          </h2>
          <p className="text-center text-gray-500 mb-8">Réponse rapide garantie</p>

          {/* Call Button in Contact Section */}
          <PremiumCallButton showMessage={true} size="large" className="mb-12" />

          <div className="text-center mb-8">
            <p className="text-gray-500">ou remplissez le formulaire ci-dessous</p>
          </div>

          <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-lg">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">Nom complet *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition text-gray-900"
                  placeholder="Votre nom"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">Téléphone *</label>
                <input
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition text-gray-900"
                  placeholder="06 12 34 56 78"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">Email *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition text-gray-900"
                  placeholder="votre@email.com"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">Message *</label>
                <textarea
                  required
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  rows="4"
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition resize-none text-gray-900"
                  placeholder="Parlez-nous de votre projet VTC..."
                />
              </div>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-lg">
                  ✓ Votre demande a bien été envoyée. Notre équipe vous contactera rapidement.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
                  ✗ Erreur lors de l'envoi. Veuillez réessayer.
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white px-8 py-4 rounded-full font-semibold text-lg transition shadow-md"
              >
                {isSubmitting ? 'Envoi en cours...' : 'Envoyer'}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 border-t border-gray-800 py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <a href="/" className="inline-block mb-4">
                <div className="flex items-baseline gap-1.5 select-none">
                  <span className="font-black tracking-tight text-white text-2xl" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
                    LOCATION
                  </span>
                  <span className="font-bold text-white text-xl" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
                    VTC
                  </span>
                  <span className="font-bold text-green-500 text-xl" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
                    IDF
                  </span>
                </div>
              </a>
              <p className="text-gray-400">
                Location de véhicules VTC Toyota C-HR hybrides en Île-de-France
              </p>
            </div>

            <div>
              <h3 className="font-bold mb-4 text-white">Services</h3>
              <ul className="space-y-2 text-gray-400">
                <li>Location VTC</li>
                <li>Location voiture VTC</li>
                <li>Voiture Uber</li>
                <li>Voiture Bolt</li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold mb-4 text-white">Contact</h3>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-green-500" /> 
                  <a href={telLink} className="hover:text-green-500 transition">
                    06 50 97 26 20
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-green-500" /> vpcommerceidf@gmail.com
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-green-500" /> Île-de-France
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-gray-500">
            <p>&copy; 2025 Location VTC Île-de-France. Location de véhicules VTC en Île-de-France.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default VTCLanding;
